import React from 'react';
import { style } from 'typestyle';
import { BasicItem } from 'src/worker/pivotWorker.types';
import { TEAL_PRIMARY, secondaryNeutral, mediumBorderColor } from 'src/common-ui/theme';
import { get, isNil } from 'lodash';
import { Renderer } from 'src/utils/Domain/Renderer';



export interface BarFillRendererProps {
  value: number;
  /** Put a valid css color here if you want a static color for all bars */
  color?: string;
  /** Pass this down from config if you want dynamic per-row colors */
  colorDataIndex?: string;
  /** Optional renderer to display the value on the left side of the bar */
  innerRenderer?: string;
  item?: BasicItem;
}
export interface BarFillRendererParams extends Pick<BarFillRendererProps, 'color' | 'colorDataIndex' | 'innerRenderer'> {}

const BarFillRenderer: React.FC<BarFillRendererProps> = ({
  value,
  color,
  colorDataIndex,
  innerRenderer,
  item
}) => {
  if (isNil(value) || !Number.isFinite(value)) {
    return null; // bail out if bad
  }

  // Clamp value between 0 and 1.0
  // This component currently expects values between 0 and 1.0
  // to represent percentages.  This is an accepted limitation of the component
  // to optimize for development speed, and should be reviewed in the future
  // to support more numbers
  const clampedValue = Math.max(0, Math.min(1, value));
  const percentage = clampedValue * 100;

  // Determine bar color, default to teal if none is provided
  let barColor = TEAL_PRIMARY;

  if (color) {
    barColor = color;
  } else if (colorDataIndex && item) {
    const dynamicColor = get(item, colorDataIndex, barColor);
    if (!isNil(dynamicColor) && typeof dynamicColor === 'string') {
      barColor = dynamicColor;
    }
  }

  // Render the inner value if innerRenderer is provided
  let renderedInnerValue: React.ReactNode = null;
  if (innerRenderer && Renderer[innerRenderer as keyof typeof Renderer]) {
    const rendererFn = Renderer[innerRenderer as keyof typeof Renderer] as any;
    renderedInnerValue = rendererFn(value, undefined, [], item);
  }

  // Wrapper style for the entire component (includes label + bar)
  const wrapperClass = style({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
  });

  // Label style class
  const labelClass = style({
    textAlign: 'right',
    flexShrink: 0,
  });

  // Container style class
  const containerClass = style({
    flex: 1,
    height: '20px',
    backgroundColor: secondaryNeutral,
    border: `1px solid ${mediumBorderColor}`,
    borderRadius: '2px',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  });

  // Fill bar style class
  const fillClass = style({
    height: '100%',
    width: `${percentage}%`,
    backgroundColor: barColor,
    transition: 'width 0.3s ease',
    borderRadius: '1px',
  });

  // If we have an inner renderer, wrap everything with the label
  if (renderedInnerValue) {
    return (
      <div className={wrapperClass}>
        <div className={labelClass}>{renderedInnerValue}</div>
        <div className={containerClass}>
          <div className={fillClass} />
        </div>
      </div>
    );
  }

  // Otherwise, just return the bar
  return (
    <div className={containerClass}>
      <div className={fillClass} />
    </div>
  );
};

export default BarFillRenderer;
